import {
  Box,
  Center,
  Flex,
  Heading,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { SourcingEventStatus, useListCombinedEventsForBuyerQuery } from "../../autogen/bff-api";
import { Paging } from "../../common/Paging";
import { useLoggedInWithOrgContextState } from "../../common/auth/useLoggedInWithOrgContextState";
import { useDisplayDate } from "../../common/formatting/useDisplayDate";
import { BUCKET_META, STATUSES_BY_BUCKET, isBucketKey, type BucketKey } from "../../common/layout/v2/statusBuckets";
import { useNavLabel } from "../../common/layout/v2/useNavLabel";
import { useSub } from "../../common/subscription/useSub";
import { useEventNavigation } from "../sourcing-events/useEventNavigation";
import { CombinedEventStatusTag } from "./sourcing-event/CombinedEventStatusTag";

const PAGE_SIZE = 10;

const STATUS_FILTERS: Record<string, { statuses: SourcingEventStatus[]; labelKey: string }> = Object.fromEntries(
  (Object.keys(BUCKET_META) as BucketKey[]).map((key) => [
    key,
    { statuses: STATUSES_BY_BUCKET[key], labelKey: BUCKET_META[key].labelKey },
  ]),
);

export const SourcingEventsList = () => {
  const { t } = useTranslation();
  const displayDate = useDisplayDate();
  const navLabel = useNavLabel();
  const sub = useSub();
  const authState = useLoggedInWithOrgContextState();
  const navigateToEvent = useEventNavigation();
  const [searchParams] = useSearchParams();
  const [skip, setSkip] = useState(0);
  const statusParam = searchParams.get("status");

  const [filteredStatus, setFilteredStatus] = useState(statusParam);
  if (statusParam !== filteredStatus) {
    setFilteredStatus(statusParam);
    setSkip(0);
  }

  const filter = isBucketKey(statusParam) ? STATUS_FILTERS[statusParam] : undefined;

  const { data, isLoading, isError, isFetching } = useListCombinedEventsForBuyerQuery(
    sub.hasSourcingEventV2Access
      ? { orgId: authState.selectedOrg.id, statuses: filter?.statuses, limit: PAGE_SIZE, skip }
      : skipToken,
  );

  const lastLoadedPage = useRef<typeof data>(undefined);
  if (data) lastLoadedPage.current = data;
  const page = data ?? lastLoadedPage.current;
  const events = page?.events ?? [];
  const count = page?.count;

  if (!sub.hasSourcingEventV2Access) {
    return (
      <Center py="20">
        <Text color="smMuted">{t("Your organization does not have access to this feature.")}</Text>
      </Center>
    );
  }

  return (
    <Box maxW="container.xl" mx="auto">
      <Heading as="h1" size="lg" color="smText" mb={filter ? "6" : "2"}>
        {filter ? navLabel(filter.labelKey) : t("All procurements")}
      </Heading>
      {!filter && (
        <Text color="smMuted" mb="6">
          {t("All your sourcing events.")}
        </Text>
      )}

      <Box bg="smBackground" rounded="xl" border="1px solid" borderColor="smBorder" overflow="hidden">
        {!page && isLoading && (
          <Flex justify="center" align="center" py="16">
            <Spinner color="smPrimary" />
          </Flex>
        )}

        {!page && isError && (
          <Flex justify="center" py="16">
            <Text color="smError">{t("Could not load sourcing events.")}</Text>
          </Flex>
        )}

        {!isError && page && count === 0 && (
          <Flex justify="center" py="16">
            <Text color="smMuted">{t("No sourcing events yet.")}</Text>
          </Flex>
        )}

        {!isError && events.length > 0 && (
          <TableContainer>
            <Table variant="simple">
              <Thead>
                <Tr>
                  <Th>{t("Title")}</Th>
                  <Th>{t("Status")}</Th>
                  <Th>{t("Deadline")}</Th>
                  <Th>{t("Created")}</Th>
                </Tr>
              </Thead>
              <Tbody>
                {events.map((event) => (
                  <Tr
                    key={event.id}
                    cursor="pointer"
                    _hover={{ bg: "smBackgroundSecondary" }}
                    onClick={() => navigateToEvent(event)}
                  >
                    <Td fontWeight="medium" color="smText">
                      {event.title || t("Untitled")}
                    </Td>
                    <Td>
                      <CombinedEventStatusTag event={event} size="sm" />
                    </Td>
                    <Td color="smMuted">{event.deadline ? displayDate(event.deadline) : "–"}</Td>
                    <Td color="smMuted">{displayDate(event.createdAt)}</Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}

        {!isError && (count ?? 0) > 0 && (
          <Paging limit={PAGE_SIZE} skip={skip} setSkip={setSkip} isLoading={isFetching} count={count} />
        )}
      </Box>
    </Box>
  );
};
